import { money, when, RIDE_TYPES } from "@/lib/rides";
import { FareSummary, Line, PAY_LABEL } from "@/components/booking/CheckoutPanel";
import { DAY_NAMES } from "@/lib/rider";

const KIND_LABEL = { ...RIDE_TYPES, split: "Split Trip" };
const Row = ({ label, children, testid }) => (
  <div className="flex justify-between gap-4 border-b border-white/5 py-2 text-sm last:border-0">
    <span className="shrink-0 text-muted-foreground">{label}</span><span className="text-right font-semibold" data-testid={testid}>{children}</span>
  </div>
);

function SplitFare({ quote, co, method }) {
  return (
    <div className="space-y-1.5 rounded-2xl border border-primary/40 bg-card p-4" data-testid="fare-summary">
      {quote.legs.map((l, i) => <Line key={i} label={`Leg ${i + 1} fare${quote.pairs > 1 ? ` × ${quote.pairs}` : ""}`} value={money(l.final_fare)} testid={`summary-leg-${i + 1}`} />)}
      {co.recurring_discount > 0 && <Line label="Recurring discount" value={`-${money(co.recurring_discount)}`} testid="summary-recurring" />}
      <Line label="Subtotal" value={money(co.subtotal)} testid="summary-subtotal" />
      <Line label={co.discount ? `Discount (${co.discount.code})` : "Discount"} value={`-${money(co.discount?.amount || 0)}`} testid="summary-discount" />
      <Line label="Booking fee" value="$0.00" testid="summary-booking-fee" />
      <div className="border-t pt-2"><Line strong label="TOTAL" value={money(co.total)} testid="summary-total" /></div>
      {co.credit_applied > 0 && <Line label="Ride Credit applied" value={`-${money(co.credit_applied)}`} testid="summary-credit" />}
      <Line strong label={method === "pay_at_pickup" ? "PAYMENT DUE AT PICKUP" : "Amount due"} value={money(co.amount_due)} testid="summary-amount-due" />
    </div>
  );
}

export function Review({ flow, quote, opts, pickup, dropoff, stops, legs }) {
  const co = quote.checkout;
  const split = flow.kind === "split";
  const first = split ? legs[0].at : opts.scheduledAt;
  return (
    <div className="space-y-3" data-testid="booking-review">
      <div className="rounded-2xl border border-white/10 bg-card px-4 py-2">
        <Row label="Ride type" testid="review-type">{KIND_LABEL[flow.kind]}{flow.mode === "now" ? " · Book Now" : ""}</Row>
        {split ? legs.map((l, i) => (
          <Row key={i} label={`Leg ${i + 1}`} testid={`review-leg-${i + 1}`}>{when(new Date(l.at).toISOString())}<br />{l.pickup.name} → {l.dropoff.name}</Row>
        )) : (
          <>
            <Row label="Pickup" testid="review-pickup">{pickup.name}{pickup.unit_number ? ` · ${pickup.unit_number}` : ""}</Row>
            {stops.map((s, i) => <Row key={i} label={`Stop ${i + 1}`} testid={`review-stop-${i}`}>{s.place?.name} · wait {s.wait} min</Row>)}
            <Row label="Destination" testid="review-dropoff">{dropoff.name}</Row>
            <Row label={flow.mode === "now" ? "Estimated pickup" : "Date & pickup time"} testid="review-time">{first ? when(new Date(first).toISOString()) : "—"}</Row>
          </>
        )}
        {flow.repeat && <Row label="Repeats" testid="review-recurrence">{opts.days.slice().sort().map((d) => DAY_NAMES[d]).join(", ")} · {opts.weeks} week{opts.weeks > 1 ? "s" : ""} ({quote.occurrences?.length} rides)</Row>}
        <Row label="Payment" testid="review-payment">{PAY_LABEL[opts.payMethod]}{opts.payMethod === "ride_credit" && co.amount_due > 0 ? ` + ${PAY_LABEL[co.remaining_method]}` : ""}</Row>
        <Row label="Available Ride Credit" testid="review-credit">{money(co.credit_available)}</Row>
      </div>
      {split ? <SplitFare quote={quote} co={co} method={opts.payMethod} /> : <FareSummary quote={quote} co={co} method={opts.payMethod} />}
    </div>
  );
}
